import { cn } from '@/utils';
import type { LucideIcon } from 'lucide-react';

interface FilterCardProps {
  label: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  initials?: string;
  avatar?: string;
  color?: string;
  size?: 'default' | 'sm';
}

export function FilterCard({
  label,
  count,
  isSelected,
  onClick,
  icon: Icon,
  initials,
  avatar,
  color = 'cyan',
  size = 'default',
}: FilterCardProps) {
  const isSmall = size === 'sm';
  const colorClasses = {
    cyan: {
      border: 'border-cyan-500',
      bg: 'bg-cyan-500/10',
      text: 'text-cyan-400',
      ring: 'ring-cyan-500/30',
      avatarBg: 'bg-gradient-to-br from-cyan-400 to-cyan-600',
    },
    orange: {
      border: 'border-[#F78C4E]',
      bg: 'bg-[#F78C4E]/10',
      text: 'text-[#FFC09A]',
      ring: 'ring-[#F78C4E]/30',
      avatarBg: 'bg-gradient-to-br from-[#F78C4E] to-[#E67A3C]',
    },
    amber: {
      border: 'border-amber-500',
      bg: 'bg-amber-500/10',
      text: 'text-amber-400',
      ring: 'ring-amber-500/30',
      avatarBg: 'bg-gradient-to-br from-amber-400 to-amber-600',
    },
    blue: {
      border: 'border-blue-500',
      bg: 'bg-blue-500/10',
      text: 'text-blue-400',
      ring: 'ring-blue-500/30',
      avatarBg: 'bg-gradient-to-br from-blue-400 to-blue-600',
    },
    purple: {
      border: 'border-purple-500',
      bg: 'bg-purple-500/10',
      text: 'text-purple-400',
      ring: 'ring-purple-500/30',
      avatarBg: 'bg-gradient-to-br from-purple-400 to-purple-600',
    },
    pink: {
      border: 'border-pink-500',
      bg: 'bg-pink-500/10',
      text: 'text-pink-400',
      ring: 'ring-pink-500/30',
      avatarBg: 'bg-gradient-to-br from-pink-400 to-pink-600',
    },
    green: {
      border: 'border-emerald-500',
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-400',
      ring: 'ring-emerald-500/30',
      avatarBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
    },
    red: {
      border: 'border-red-500',
      bg: 'bg-red-500/10',
      text: 'text-red-400',
      ring: 'ring-red-500/30',
      avatarBg: 'bg-gradient-to-br from-red-400 to-red-600',
    },
  };

  const colors = colorClasses[color as keyof typeof colorClasses] || colorClasses.cyan;

  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex flex-col items-center transition-all',
        isSmall
          ? 'gap-1 rounded-lg border px-2 py-1.5 min-w-[52px]'
          : 'gap-2 rounded-xl border-2 px-4 py-3 min-w-[72px]',
        isSelected
          ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}`
          : 'border-zinc-700/50 bg-zinc-800/80 hover:border-zinc-600 hover:bg-zinc-800'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex items-center justify-center rounded-full font-semibold transition-all',
          isSmall ? 'h-6 w-6 text-[10px]' : 'h-10 w-10 text-sm',
          isSelected
            ? `${colors.avatarBg} text-white`
            : avatar
            ? ''
            : 'bg-zinc-700 text-zinc-400 group-hover:bg-zinc-600'
        )}
      >
        {avatar ? (
          <img
            src={avatar}
            alt={label}
            className="h-full w-full rounded-full object-cover"
          />
        ) : Icon ? (
          <Icon className={isSmall ? 'h-3 w-3' : 'h-5 w-5'} />
        ) : (
          initials || label.charAt(0).toUpperCase()
        )}
      </div>

      {/* Label */}
      <span
        className={cn(
          'font-medium whitespace-nowrap',
          isSmall ? 'text-[10px]' : 'text-xs',
          isSelected ? colors.text : 'text-zinc-300'
        )}
      >
        {label}
      </span>

      {/* Count */}
      <span
        className={cn(
          'font-bold',
          isSmall ? 'text-sm' : 'text-lg',
          isSelected ? 'text-white' : 'text-zinc-400'
        )}
      >
        {count}
      </span>
    </button>
  );
}
